import { Award, Factory, Ruler, Truck, MapPin, ShieldCheck } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import MicroTrust from "@/components/MicroTrust";
import { Button } from "@/components/ui/button";

interface WhyChooseUsSectionProps {
  onAction?: (action: string) => void;
}

const items = [
  {
    icon: Award,
    metric: "30+",
    label: "лет на рынке",
    desc: "С 1993 года в Ташкенте. Знаем каждый ЖК, каждый тип окон.",
  },
  {
    icon: Factory,
    metric: "100%",
    label: "собственное производство",
    desc: "Шьём сами — без посредников, цена честная, сроки короткие.",
  },
  {
    icon: Ruler,
    metric: "0 сум",
    label: "замер по Ташкенту",
    desc: "Замерщик приедет с каталогом 120+ тканей. Без обязательств.",
  },
  {
    icon: Truck,
    metric: "3 дня",
    label: "от замера до монтажа",
    desc: "Стандартный заказ изготавливаем за 2 дня, ставим на 3-й.",
  },
  {
    icon: MapPin,
    metric: "11/11",
    label: "районов Ташкента",
    desc: "Юнусабад, Чиланзар, Мирзо-Улугбек, Сергели, Яккасарай — везде.",
  },
  {
    icon: ShieldCheck,
    metric: "1 год",
    label: "гарантия на изделия",
    desc: "Сломалась цепочка или механизм — заменим бесплатно.",
  },
];

const WhyChooseUsSection = ({ onAction }: WhyChooseUsSectionProps) => {
  const handleCta = () => {
    trackEvent("open_lead_form", { lead_type: "why_us_cta" });
    onAction?.("why_us_cta");
  };

  return (
    <section className="px-4 md:px-0 py-1 md:py-3">
      <div className="text-center mb-5 md:mb-7">
        <span className="inline-block text-[10px] md:text-[11px] font-bold tracking-[0.18em] uppercase text-primary mb-1.5">
          Почему именно Zebra Jaluzi
        </span>
        <h2 className="text-[20px] md:text-3xl font-bold text-foreground tracking-tight leading-tight">
          6 причин не выбирать «случайных мастеров»
        </h2>
        <p className="text-[12px] md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl mx-auto">
          Делаем жалюзи в Ташкенте 30+ лет. Сами производим, сами замеряем, сами ставим.
        </p>
      </div>

      <div className="continuous-marquee compact-ribbon overflow-hidden border-y border-border bg-card py-3 md:py-4" aria-label="Преимущества Zebra Jaluzi">
        <div className="continuous-marquee-track continuous-marquee-track-slow flex w-max gap-2.5 md:gap-3">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 gap-2.5 md:gap-3" aria-hidden={copy === 1}>
              {items.map(({ icon: Icon, metric, label, desc }) => (
                <div
                  key={`${copy}-${label}`}
                  className="w-[220px] md:w-64 shrink-0 rounded-lg bg-card p-3.5 md:p-4 border border-border/70"
                  style={{ boxShadow: "var(--shadow-card)" }}
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" strokeWidth={1.9} />
                    </div>
                    <div className="text-[17px] md:text-lg font-extrabold text-foreground leading-none">
                      {metric}
                    </div>
                  </div>
                  <div className="text-[9px] md:text-[10px] font-bold text-primary uppercase tracking-wide">{label}</div>
                  <p className="text-[11px] md:text-xs text-muted-foreground mt-1 leading-snug">{desc}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {onAction && (
        <div className="mt-5 md:mt-7 flex flex-col items-center gap-2.5">
          <Button
            onClick={handleCta}
            className="group h-11 rounded-lg px-6 md:px-8 text-[13px] md:text-sm font-semibold"
          >
            Получить расчёт за 2 минуты
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </Button>
          <MicroTrust />
          <p className="text-[12px] md:text-[13px] text-muted-foreground">
            Запишитесь сегодня — приедем уже завтра
          </p>
        </div>
      )}
    </section>
  );
};

export default WhyChooseUsSection;
